import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { BlackoutDraft } from '~/components/PricingTool/tabs/blackouts/types';
import type { EgsDraftPayload } from '~/components/PricingTool/tabs/egs/types';
import type { ForecastSessionState } from '~/components/PricingTool/tabs/forecasting/types';
import type { InclusionsDraft } from '~/components/PricingTool/tabs/inclusions/types';
import type { SavedOverridesValues } from '~/components/PricingTool/tabs/overrides/types';
import type { PackageOptionsDraft } from '~/components/PricingTool/tabs/packageOptions/types';
import type { SavedOtaSummary } from '~/components/PricingTool/tabs/pricingSummary/useOtaSummary';
import type { PropertySetupValues } from '~/components/PricingTool/tabs/propertySetup/types';
import type { SurchargeSessionState } from '~/components/PricingTool/tabs/surcharges/types';
import type { PricingOptionIndex } from '~/components/PricingTool/types';

import OffersService from '~/services/OffersService';

import ApiError from '~/types/ApiError';

import { offerQueryKeys } from './useGetOffer';

export interface PricingToolDraftPayload {
  propertySetup?: PropertySetupValues;
  blackouts?: Array<Pick<BlackoutDraft, 'id' | 'name' | 'startDate' | 'endDate'>>;
  overrides?: SavedOverridesValues;
  preferredOption?: PricingOptionIndex | null;
  surcharges?: SurchargeSessionState;
  packageOptions?: PackageOptionsDraft;
  inclusions?: InclusionsDraft;
  forecasting?: ForecastSessionState;
  egs?: Array<EgsDraftPayload>;
  otaSummary?: SavedOtaSummary;
}

export interface PricingToolDraft {
  opportunity_id: string;
  payload: PricingToolDraftPayload;
  version: number;
  created_at: string;
  updated_at: string;
}

export function useGetPricingToolDraft(opportunityId: string | undefined) {
  return useQuery({
    queryKey: offerQueryKeys.pricingToolDraft(opportunityId),
    queryFn: async () => {
      if (!opportunityId) {
        throw new Error('opportunityId is required');
      }

      try {
        const response = await OffersService.getPricingToolDraft(opportunityId);
        return response.result as PricingToolDraft;
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) {
          return null;
        }

        throw error;
      }
    },
    enabled: !!opportunityId,
  });
}

export function useSavePricingToolDraft(opportunityId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ version, payload }: { version: number; payload: PricingToolDraftPayload }) => {
      if (!opportunityId) {
        throw new Error('opportunityId is required');
      }

      const response = await OffersService.savePricingToolDraft(opportunityId, version, payload);
      return response.result as PricingToolDraft;
    },
    onSuccess: (draft) => {
      queryClient.setQueryData(offerQueryKeys.pricingToolDraft(opportunityId), draft);
    },
  });
}

export function useDeletePricingToolDraft(opportunityId: string | undefined) {
  return useMutation({
    mutationFn: async (version: number) => {
      if (!opportunityId) {
        throw new Error('opportunityId is required');
      }

      return OffersService.deletePricingToolDraft(opportunityId, version);
    },
  });
}
